import { JSX } from 'preact'
import { useMemo, useState } from 'preact/hooks'
import classnames from 'classnames'
import { CSSRule, ActionType } from '@gsc/types'
import { useResizeObserver } from '../../hooks'
import { useActions } from '../useActions'
import { Form } from '../Form'
import { StyleImages } from '../StyleImage/StyleImages'
import { useRegisterStyle } from '../Style'
import { SystemButton } from '../SystemButton'
import { Logo } from '../Logo'
import { Header } from './Header'
import { LayoutProps } from './interfaces'
import { BlockTree } from '../Blocks/BlockTree'

type Props = Omit<LayoutProps, 'layout'>

export function PanelLayout(props: Props): JSX.Element {
  const { hasPreviousPage, page, expanded, className, innerRef, closeButton, backButton, header, version } = props
  const { root } = page

  const isV2 = version === 'V2' // we can't rely on presence of header for V2, as it migrates in renderer

  const processActions = useActions()
  const [isExpanded, setIsExpanded] = useState(expanded)

  const [{ height: headerHeight }, setHeaderSize] = useState({ width: 0, height: 0 })
  const { ref: headerRef } = useResizeObserver(setHeaderSize)

  const style = useMemo<CSSRule>(
    () =>
      headerHeight !== 0 ? { id: 'root', selector: '.root', css: { '--g-header-height': `${headerHeight}px` } } : null,
    [headerHeight],
  )
  useRegisterStyle(style)

  const renderHeader = () => {
    const hasToggle = closeButton?.visible ?? true
    const hasBack = (backButton?.visible ?? true) && hasPreviousPage

    const toggleExpanded = (e: MouseEvent) => {
      e.stopPropagation()
      setIsExpanded(prevState => !prevState)
    }

    const navigateBack = (e: Event) => {
      e.stopPropagation()
      return processActions([{ type: ActionType.Back }])
    }

    return (
      <Header ref={headerRef} header={header} onClick={toggleExpanded}>
        {hasBack && isExpanded && <SystemButton name="back" {...backButton} onClick={navigateBack} />}
        {hasToggle && (
          <SystemButton
            name="caret"
            {...closeButton}
            className={classnames(closeButton?.className, { collapsed: !isExpanded })}
          />
        )}
      </Header>
    )
  }

  const classes = classnames(className, isExpanded ? 'expanded' : 'collapsed')

  return (
    <div ref={innerRef} className={classes} role="dialog">
      {isV2 && renderHeader()}
      <Form {...root}>
        {!isV2 && renderHeader()}
        <StyleImages />
        {root.children.map(c => (
          <BlockTree root={c} />
        ))}
      </Form>
      <Logo />
    </div>
  )
}
